import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { getAppRoutes } from './routes';
import { useAuthStore } from './stores/auth';
import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import './i18n';
import { useTheme } from './hooks/useTheme';
import { Login } from './views/auth/pages/LoginPage';
import { ResetPasswordPage } from './views/auth/pages/ResetPasswordPage';
import { ForgotPasswordPage } from './views/auth/pages/ForgotPasswordPage';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import NotFound from './views/session/pages/NotFoundPage';
import { MainLayout } from './components/Layout/MainLayout';
import { LanguageProvider } from './components/LanguageProvider';
import type { AppRoute } from './routes';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { useTranslation } from 'react-i18next';
import InvitePage from './views/invite/pages/InvitePage';
import PrivacyPolicy from './views/auth/pages/PrivacyPolicy';
import ManualPage from './views/public/pages/ManualPage';
import { EstablishmentContextModal } from './components/context/EstablishmentContextModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import TeleattendancePublicPage from './views/teleattendance/public/TeleattendancePublicPage';
import ModuleGatewayPage from './views/modules/pages/ModuleGatewayPage';
import { SystemDocumentAcceptanceModal } from './components/legal/SystemDocumentAcceptanceModal';
import TermsOfUsePage from './views/legal/pages/TermsOfUsePage';
import PrivacyPolicyPage from './views/legal/pages/PrivacyPolicyPage';
import OnboardingInterviewMeetingPage from './views/onboardingInterviews/pages/OnboardingInterviewMeetingPage';
import OnboardingInterviewRecordingPage from './views/onboardingInterviews/pages/OnboardingInterviewRecordingPage';
import { RouteDiagnostics } from './components/RouteDiagnostics';

dayjs.extend(utc);
dayjs.locale('pt');

const renderRoutes = (routes: AppRoute[]) =>
  routes.map((route) => {
    if (route.children && route.children.length > 0) {
      return (
        <Route
          key={route.key}
          path={route.path}
          element={
            <ProtectedRoute>
              <route.element />
            </ProtectedRoute>
          }
        >
          {renderRoutes(route.children)}
        </Route>
      );
    }
    return (
      <Route
        key={route.key}
        path={route.path}
        element={
          <ProtectedRoute>
            <route.element />
          </ProtectedRoute>
        }
      />
    );
  });

const AppContent = () => {
  const { t } = useTranslation();
  useTheme();
  const establishment_config_id = useAuthStore(
    (s) => s.user?.establishment_config_id ?? null,
  );
  const company_config_id = useAuthStore(
    (s) => s.user?.company_config_id ?? null,
  );
  const is_config_admin = useAuthStore((s) => s.user?.is_config_admin ?? false);
  const is_company_admin = useAuthStore(
    (s) => s.user?.is_company_admin ?? false,
  );
  const is_master = useAuthStore(
    (s) => s.user?.role_id === '1' || s.user?.role === 'master',
  );
  const appRoutes = getAppRoutes(
    establishment_config_id,
    company_config_id,
    is_config_admin,
    is_company_admin,
    is_master,
  );

  return (
    <MantineProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />

        <LanguageProvider>
          <BrowserRouter
            future={{
              v7_relativeSplatPath: true,
              v7_startTransition: true,
            }}
          >
            <RouteDiagnostics />
            <EstablishmentContextModal />
            <SystemDocumentAcceptanceModal />
            <Routes>
              {/* Public Routes */}
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms-of-use" element={<TermsOfUsePage />} />
              <Route
                path="/legal/privacy-policy"
                element={<PrivacyPolicyPage />}
              />
              <Route
                path="/manuals/manual-geoescala2026.pdf"
                element={<ManualPage />}
              />
              <Route
                path="/teleattendance/public/:token"
                element={<TeleattendancePublicPage />}
              />
              <Route
                path="/onboarding-interviews/public/:token"
                element={<OnboardingInterviewMeetingPage />}
              />
              <Route
                path="/onboarding-interviews/recording/:token"
                element={<OnboardingInterviewRecordingPage />}
              />
              <Route
                path="/:legacyPrefix/access-control/facesign-events"
                element={<Navigate to="/access-control/facesign-events" replace />}
              />
              <Route
                path="/"
                element={<Navigate to="/session/login" replace />}
              />
              <Route path="/session/login" element={<Login />} />
              <Route
                path="/session/reset-password"
                element={<ResetPasswordPage />}
              />
              <Route
                path="/session/forgot-password"
                element={<ForgotPasswordPage />}
              />

              {/* Module Gateway */}
              <Route
                path="/modules"
                element={
                  <ProtectedRoute>
                    <ModuleGatewayPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/my-establishment/onboarding-interviews/calendar/:interviewId/meeting"
                element={
                  <ProtectedRoute>
                    <OnboardingInterviewMeetingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-company/onboarding-interviews/calendar/:interviewId/meeting"
                element={
                  <ProtectedRoute>
                    <OnboardingInterviewMeetingPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                {renderRoutes(appRoutes)}
              </Route>

              {/* Unauthorized route */}
              <Route
                path="/unauthorized"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-background">
                    <div className="text-center">
                      <h1 className="text-4xl font-bold mb-4 text-destructive">
                        403
                      </h1>
                      <p className="text-xl text-muted-foreground mb-4">
                        {t('unauthorized.title')}
                      </p>
                      <p className="text-muted-foreground">
                        {t('unauthorized.description')}
                      </p>
                    </div>
                  </div>
                }
              />

              {/* NotFound */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </TooltipProvider>
    </MantineProvider>
  );
};

ModuleRegistry.registerModules([AllCommunityModule]);

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};
export default App;
